"use client"

import type React from "react"
import { useEffect, useState } from "react"
import Breadcrumb from "../../../utils/breadcrumbs"
import { FiSend, FiSmartphone, FiBell, FiInfo } from "react-icons/fi"
import toast from "react-hot-toast"
import api from "../../../libs/axios"

type Target = "both" | "android" | "ios"

interface BroadcastForm {
    title: string
    body: string
    target: Target
    use_app_update_template: boolean
    android_link: string
    ios_link: string
    test_phone: string
    /** Empty = everyone. Otherwise only users on these app versions. */
    app_versions: string[]
}

interface VersionStat { app_version: string; android: number; ios: number; total: number }

// Default copy for the app-update template, so it isn't retyped on every send.
const UPDATE_TEMPLATE = {
    title: "Update available",
    body: "A new version of CrikStars is out. Please update to the latest version for the best experience.",
}

const emptyForm: BroadcastForm = {
    title: "",
    body: "",
    target: "both",
    use_app_update_template: false,
    android_link: "",
    ios_link: "",
    test_phone: "",
    app_versions: [],
}

const BroadcastNotification: React.FC = () => {
    const [form, setForm] = useState<BroadcastForm>(emptyForm)
    const [sending, setSending] = useState(false)
    const [testing, setTesting] = useState(false)
    // App versions in use, for the "only users on old builds" segment.
    const [versions, setVersions] = useState<VersionStat[]>([])

    useEffect(() => {
        api.get("/users/live/stats")
            .then((res: any) => setVersions(res?.data?.by_version || []))
            .catch(() => { /* segmenting is optional — the form still sends to everyone */ })
    }, [])

    const toggleVersion = (v: string) =>
        setForm((f) => ({
            ...f,
            app_versions: f.app_versions.includes(v)
                ? f.app_versions.filter((x) => x !== v)
                : [...f.app_versions, v],
        }))

    const set = <K extends keyof BroadcastForm>(key: K, value: BroadcastForm[K]) =>
        setForm((f) => ({ ...f, [key]: value }))

    // Turning the template ON also fills the update copy — but only into BLANK fields,
    // so anything the admin already typed is never clobbered.
    const toggleTemplate = (checked: boolean) =>
        setForm((f) => ({
            ...f,
            use_app_update_template: checked,
            title: checked && !f.title.trim() ? UPDATE_TEMPLATE.title : f.title,
            body: checked && !f.body.trim() ? UPDATE_TEMPLATE.body : f.body,
        }))

    const validate = (): boolean => {
        if (!form.title.trim()) {
            toast.error("Title is required")
            return false
        }
        if (!form.body.trim()) {
            toast.error("Message is required")
            return false
        }
        if (!form.use_app_update_template && !form.android_link.trim() && !form.ios_link.trim()) {
            toast.error("Add a tap link (Android/iOS) or use the App Update template")
            return false
        }
        return true
    }

    // Payload shared by test + broadcast.
    const payload = () => ({
        title: form.title.trim(),
        body: form.body.trim(),
        target: form.target,
        use_app_update_template: form.use_app_update_template,
        android_link: form.android_link.trim(),
        ios_link: form.ios_link.trim(),
        app_versions: form.app_versions,
    })

    const handleTest = async () => {
        if (!form.test_phone.trim()) {
            toast.error("Enter a phone number to send a test")
            return
        }
        if (!validate()) return
        setTesting(true)
        try {
            await api.post("/notifications/broadcast/test", { ...payload(), phone: form.test_phone.trim() })
            toast.success(`Test sent to ${form.test_phone.trim()}`)
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Failed to send test")
        } finally {
            setTesting(false)
        }
    }

    const handleBroadcast = async () => {
        if (!validate()) return
        const scope =
            form.target === "both" ? "ALL users (Android + iOS)" : form.target === "android" ? "all ANDROID users" : "all iOS users"
        const segment = form.app_versions.length ? ` on app version ${form.app_versions.join(", ")}` : ""
        if (!window.confirm(`Send this notification to ${scope}${segment}? This cannot be undone.`)) return
        setSending(true)
        try {
            const res: any = await api.post("/notifications/broadcast", payload())
            const total = res?.data?.total
            toast.success(
                typeof total === "number" ? `Broadcast queued to ~${total} device(s)` : "Broadcast queued",
            )
            setForm((f) => ({ ...emptyForm, test_phone: f.test_phone }))
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Failed to send broadcast")
        } finally {
            setSending(false)
        }
    }

    const inputCls =
        "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
    const labelCls = "block text-sm font-medium text-gray-700 mb-1"

    return (
        <div className="p-4 md:p-6">
            <Breadcrumb />

            <div className="mx-auto mt-4 max-w-2xl">
                <div className="mb-4 flex items-center gap-2 text-gray-800">
                    <FiBell className="h-5 w-5 text-blue-600" />
                    <h2 className="text-lg font-semibold">Broadcast a push to all users</h2>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    {/* Title */}
                    <div className="mb-4">
                        <label className={labelCls}>Title</label>
                        <input
                            className={inputCls}
                            value={form.title}
                            maxLength={200}
                            placeholder="e.g. Update available"
                            onChange={(e) => set("title", e.target.value)}
                        />
                    </div>

                    {/* Body */}
                    <div className="mb-4">
                        <label className={labelCls}>Message</label>
                        <textarea
                            className={`${inputCls} min-h-[90px] resize-y`}
                            value={form.body}
                            maxLength={1000}
                            placeholder="e.g. Please update to the latest version for the best experience."
                            onChange={(e) => set("body", e.target.value)}
                        />
                    </div>

                    {/* Target */}
                    <div className="mb-4">
                        <label className={labelCls}>Send to</label>
                        <div className="flex gap-2">
                            {(["both", "android", "ios"] as Target[]).map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => set("target", t)}
                                    className={`rounded-lg border px-4 py-2 text-sm capitalize ${form.target === t
                                        ? "border-blue-600 bg-blue-50 text-blue-700"
                                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                                        }`}
                                >
                                    {t === "both" ? "Both" : t}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* App-version segment — the point of this is reaching users stuck on an
                        OLD build, who are exactly the ones who never see an update prompt. */}
                    {versions.length > 0 && (
                        <div className="mb-4">
                            <label className={labelCls}>App version (optional)</label>
                            <div className="flex flex-wrap gap-2">
                                {versions.map((v) => {
                                    const active = form.app_versions.includes(v.app_version)
                                    return (
                                        <button
                                            key={v.app_version}
                                            type="button"
                                            onClick={() => toggleVersion(v.app_version)}
                                            title={`Android ${v.android} · iOS ${v.ios}`}
                                            className={`rounded-full border px-3 py-1.5 text-sm ${active
                                                ? "border-blue-600 bg-blue-50 text-blue-700"
                                                : "border-gray-300 text-gray-600 hover:bg-gray-50"
                                                }`}
                                        >
                                            {v.app_version}{" "}
                                            <span className={active ? "text-blue-500" : "text-gray-400"}>{v.total.toLocaleString()}</span>
                                        </button>
                                    )
                                })}
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                {form.app_versions.length === 0
                                    ? "Nothing selected → sends to every version."
                                    : `Only users on ${form.app_versions.join(", ")} will get this.`}
                            </p>
                        </div>
                    )}

                    {/* App-update template toggle */}
                    <label className="mb-4 flex cursor-pointer items-start gap-2">
                        <input
                            type="checkbox"
                            className="mt-1"
                            checked={form.use_app_update_template}
                            onChange={(e) => toggleTemplate(e.target.checked)}
                        />
                        <span className="text-sm text-gray-700">
                            <span className="font-medium">Use App Update template</span>
                            <span className="block text-xs text-gray-500">
                                Fills the update title/message, and takes the store links from the saved config
                                (Settings → App Links) — nothing to type or paste. You can still edit the text.
                            </span>
                        </span>
                    </label>

                    {/* Tap links (hidden when using the template) */}
                    {!form.use_app_update_template && (
                        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div>
                                <label className={labelCls}>Android tap link</label>
                                <input
                                    className={inputCls}
                                    value={form.android_link}
                                    placeholder="https://play.google.com/..."
                                    onChange={(e) => set("android_link", e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>iOS tap link</label>
                                <input
                                    className={inputCls}
                                    value={form.ios_link}
                                    placeholder="https://apps.apple.com/..."
                                    onChange={(e) => set("ios_link", e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    <div className="mb-5 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-700">
                        <FiInfo className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <span>
                            Each device opens the link for its own platform. The notification also appears in the app&apos;s
                            <span className="font-medium"> Updates</span> tab.
                        </span>
                    </div>

                    {/* Test send */}
                    <div className="mb-5 rounded-lg border border-gray-200 p-3">
                        <label className={labelCls}>
                            <FiSmartphone className="mr-1 inline h-4 w-4" />
                            Test to a phone number
                        </label>
                        <div className="flex gap-2">
                            <input
                                className={inputCls}
                                value={form.test_phone}
                                placeholder="Phone number (exact, as stored)"
                                onChange={(e) => set("test_phone", e.target.value)}
                            />
                            <button
                                type="button"
                                onClick={handleTest}
                                disabled={testing}
                                className="whitespace-nowrap rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                                {testing ? "Sending…" : "Send Test"}
                            </button>
                        </div>
                    </div>

                    {/* Broadcast */}
                    <button
                        type="button"
                        onClick={handleBroadcast}
                        disabled={sending}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                        <FiSend className="h-4 w-4" />
                        {sending ? "Sending…" : "Send to all users"}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default BroadcastNotification
